import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BadgeCheck, HardHat } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toAppError } from '../../api/errors';
import { Routes } from '../../app/routes';
import { flatten, keys, useCatalog, useEmployee, useInbox, useItems, useSetAutonomy } from '../../data/queries';
import type { AutonomyKind, Employee, KindAutonomy, Workspace } from '../../domain/models';
import { isWorking } from '../../domain/models';
import { useT } from '../../l10n/i18n';
import { errorMessage, headerTint, workKindIcon, workKindName } from '../core/presentation';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { EmptyState, ErrorView, Pill, Skeleton } from '../core/widgets/common';
import { useToast } from '../core/widgets/toasts';
import { LoadMore } from '../history/LoadMore';
import { WorkEntries } from '../inbox/WorkEntries';
import { ConfirmDialog } from '../item/dialogs';
import { ProfileCard, SkillsCard } from './ProfileCard';
import {
  autonomyChange,
  autonomyConfirm,
  autonomyNote,
  employeeTabs,
  switchableKinds,
  tabEntries,
  teamHref,
  tint,
  type EmployeeTab,
} from './team';
import './team.css';

// One employee: who they are, what waits on the person, the work handed to them, and everything they
// did (the mobile app's employee screen). The open tab lives in the address (`?tab=all`).

export function EmployeePage() {
  const { id = '' } = useParams<{ id: string }>();
  const t = useT();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  const employeeQ = useEmployee(id);
  const e = employeeQ.data;
  // What the employee does and what it asks the business come from its module on the shelf.
  const module = useCatalog(e !== undefined).data?.find((m) => m.role === e?.role);
  const takesTasks = e?.takesTasks ?? false;
  const waitingQ = useInbox({ employeeId: id });
  const allQ = useItems({ employeeId: id });
  const tasksQ = useItems({ employeeId: id, type: 'task' }, takesTasks);

  const tabs = employeeTabs(e);
  const asked = params.get('tab') as EmployeeTab | null;
  const tab: EmployeeTab = asked && tabs.includes(asked) ? asked : 'waiting';
  const select = (next: EmployeeTab) =>
    setParams(next === 'waiting' ? {} : { tab: next }, { replace: true, preventScrollReset: true });

  const waiting = flatten(waitingQ.data);
  const tasks = flatten(tasksQ.data);
  // A list with more pages than loaded counts what the server counted.
  const waitingCount = waiting
    ? waitingQ.hasNextPage
      ? Math.max(waiting.length, e?.openCount ?? 0)
      : waiting.length
    : (e?.openCount ?? 0);
  const workingCount = tasks ? tasks.filter(isWorking).length : (e?.inProgressCount ?? 0);

  if (!e && employeeQ.isError) {
    return (
      <div className="page">
        <ErrorView error={employeeQ.error} onRetry={() => void employeeQ.refetch()} />
      </div>
    );
  }

  // Back to the team, on this employee's workspace when the app knows it.
  const workspace = e && qc.getQueryData<Workspace[]>(keys.workspaces)?.find((w) => w.id === e.workspaceId);
  const back = teamHref(e && (workspace?.slug ?? e.workspaceId));

  const tabLabel = (tb: EmployeeTab) => {
    switch (tb) {
      case 'waiting':
        return `${t('employeeTabWaiting')}${waitingCount > 0 ? ` · ${waitingCount}` : ''}`;
      case 'tasks':
        return `${t('employeeTabTasks')}${workingCount > 0 ? ` · ${workingCount}` : ''}`;
      case 'all':
        return t('employeeTabAll');
    }
  };

  const onTabKey = (ev: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.indexOf(tab);
    const n = tabs.length;
    const next = {
      ArrowRight: tabs[(i + 1) % n],
      ArrowLeft: tabs[(i - 1 + n) % n],
      Home: tabs[0],
      End: tabs[n - 1],
    }[ev.key];
    if (!next) return;
    ev.preventDefault();
    select(next);
    document.getElementById(`emp-tab-${next}`)?.focus();
  };

  const q = tab === 'waiting' ? waitingQ : tab === 'tasks' ? tasksQ : allQ;
  const items = tab === 'waiting' ? waiting : tab === 'tasks' ? tasks : flatten(allQ.data);

  let list;
  if (!items && q.isError) {
    list = <ErrorView error={q.error} onRetry={() => void q.refetch()} />;
  } else if (!items) {
    list = (
      <div className="emp-skeletons">
        {[0, 1, 2].map((i) => (
          <div key={i} className="card emp-skeleton-card">
            <Skeleton width={44} height={44} radius={15} />
            <div>
              <Skeleton width={120} height={11} />
              <Skeleton height={16} />
              <Skeleton width={200} height={12} />
            </div>
          </div>
        ))}
      </div>
    );
  } else if (items.length === 0) {
    list = (
      <EmptyState
        art={e && <EmployeeAvatar avatar={e.avatar} color={e.color} size={56} mood="happy" />}
        title={{ waiting: t('inboxEmptyTitle'), tasks: t('employeeNoTasks'), all: t('employeeEmpty') }[tab]}
      />
    );
  } else {
    list = (
      <>
        <div className="card emp-list">
          <WorkEntries
            entries={tabEntries(tab, items)}
            isOpen={(key) => open.has(key)}
            toggle={(key) =>
              setOpen((was) => {
                const now = new Set(was);
                if (!now.delete(key)) now.add(key);
                return now;
              })
            }
            itemHref={Routes.item}
            showStatus={tab !== 'waiting'}
          />
        </div>
        <LoadMore hasMore={q.hasNextPage} loading={q.isFetchingNextPage} onMore={() => void q.fetchNextPage()} />
      </>
    );
  }

  return (
    <div>
      <header
        className="emp-hero"
        style={{ background: e ? `linear-gradient(to bottom, ${headerTint(e.color)}, var(--canvas))` : undefined }}
      >
        <div className="page">
          <Link to={back} className="btn btn-text btn-small emp-back">
            <ArrowLeft size={18} aria-hidden />
            {t('navTeam')}
          </Link>
          {e ? (
            <div className="emp-who">
              <EmployeeAvatar avatar={e.avatar} color={e.color} size={96} animate label={e.name} />
              <h1 className="headline-medium">{e.name}</h1>
              <p className="body-medium muted">{e.title}</p>
              {e.takesTasks && (
                <div className="emp-pills">
                  <Pill label={t('teamTakesTasks')} icon={HardHat} color={e.color} background={tint(e.color)} />
                  {workingCount > 0 && (
                    <Pill
                      label={t('teamWorking', { count: workingCount })}
                      color="var(--accent)"
                      background="var(--accent-soft)"
                      dot="var(--accent)"
                      pulse
                    />
                  )}
                </div>
              )}
              {e.description && <p className="body-medium muted emp-about pre-wrap">{e.description}</p>}
              {module && module.skills.length > 0 && <SkillsCard module={module} />}
              <ProfileCard employee={e} module={module} />
              {/* Nothing it carries out itself, nothing to automate: no card. */}
              {switchableKinds(e).length > 0 && <AutonomySettings employee={e} />}
            </div>
          ) : (
            <div className="emp-skeleton">
              <Skeleton width={96} height={96} radius={32} />
              <Skeleton width={160} />
            </div>
          )}
        </div>
      </header>
      <div className="page emp-body">
        <div className="emp-tabs" role="tablist" aria-label={e?.name} onKeyDown={onTabKey}>
          {tabs.map((tb) => (
            <button
              key={tb}
              id={`emp-tab-${tb}`}
              type="button"
              role="tab"
              className="emp-tab"
              aria-selected={tb === tab}
              aria-controls="emp-panel"
              tabIndex={tb === tab ? 0 : -1}
              onClick={() => select(tb)}
            >
              {tabLabel(tb)}
            </button>
          ))}
        </div>
        <div id="emp-panel" role="tabpanel" aria-labelledby={`emp-tab-${tab}`}>
          {list}
        </div>
      </div>
    </div>
  );
}

/** Which kinds of the employee's work are approved without a person, one row per kind. Switches are
 * there only when the server offers `set_autonomy` (ADR 0008); anyone else sees the state. Turning a
 * kind on asks first, since its work then goes out unseen; turning it off does not. */
function AutonomySettings({ employee: e }: { employee: Employee }) {
  const t = useT();
  const toast = useToast();
  const id = useId();
  const setAutonomy = useSetAutonomy();
  // Kinds being saved: only their rows hold still.
  const [saving, setSaving] = useState<ReadonlySet<AutonomyKind>>(new Set());
  const [confirming, setConfirming] = useState<AutonomyKind>();

  // Once the question is answered, the keyboard is back on that row's switch.
  const asked = useRef<AutonomyKind | undefined>(undefined);
  useEffect(() => {
    if (confirming) asked.current = confirming;
    else if (asked.current) {
      document.getElementById(`${id}-${asked.current}`)?.focus();
      asked.current = undefined;
    }
  }, [confirming, id]);

  const change = async (kind: AutonomyKind, automatic: boolean) => {
    setConfirming(undefined);
    setSaving((was) => new Set(was).add(kind));
    try {
      await setAutonomy.mutateAsync({ employeeId: e.id, kind, automatic });
      const name = workKindName(t, kind);
      toast(
        !automatic
          ? t('autonomyTurnedOff', { kind: name })
          : kind === 'request'
            ? t('autonomyTurnedOnRequest')
            : t('autonomyTurnedOn', { kind: name }),
      );
    } catch (err) {
      // Nothing changed, so the switch stays where it was.
      toast(errorMessage(t, toAppError(err)));
    } finally {
      setSaving((was) => {
        const now = new Set(was);
        now.delete(kind);
        return now;
      });
    }
  };

  const toggle = (k: KindAutonomy) => {
    if (saving.has(k.kind)) return;
    if (k.automatic) void change(k.kind, false);
    else setConfirming(k.kind);
  };

  const changed = autonomyChange(t, e);
  return (
    <section className="card emp-autonomy" aria-labelledby={`${id}-title`} data-tour="autonomy">
      <div className="emp-autonomy-head">
        <h2 id={`${id}-title`} className="title-medium">
          {t('autonomyTitle')}
        </h2>
        {e.canSetAutonomy && <p className="body-small">{t('autonomyHint')}</p>}
        {changed && <p className="body-small faint">{changed}</p>}
      </div>
      <ul className="emp-autonomy-rows">
        {switchableKinds(e).map((k) => {
          const Icon = workKindIcon(k.kind);
          const labelId = `${id}-${k.kind}-label`;
          const note = autonomyNote(t, k);
          const noteId = note ? `${id}-${k.kind}-note` : undefined;
          let control;
          if (e.canSetAutonomy) {
            // aria-disabled rather than disabled while it saves, so keyboard focus stays on it.
            control = (
              <button
                id={`${id}-${k.kind}`}
                type="button"
                role="switch"
                className="switch"
                aria-checked={k.automatic}
                aria-labelledby={labelId}
                aria-describedby={noteId}
                aria-disabled={saving.has(k.kind)}
                onClick={() => toggle(k)}
              >
                <span className="switch-thumb" />
              </button>
            );
          } else {
            // The state only: nothing to press.
            control = (
              <span
                role="switch"
                className="switch readonly"
                aria-checked={k.automatic}
                aria-readonly
                aria-labelledby={labelId}
                aria-describedby={noteId}
              >
                <span className="switch-thumb" />
              </span>
            );
          }
          return (
            <li key={k.kind} className={k.automatic ? 'emp-autonomy-row on' : 'emp-autonomy-row'} data-tour={`autonomy-${k.kind}`}>
              <span className="emp-autonomy-tile" aria-hidden>
                <Icon size={18} strokeWidth={2} />
              </span>
              <span className="emp-autonomy-text">
                <span id={labelId} className="title-small">
                  {workKindName(t, k.kind)}
                </span>
                {note && (
                  <span id={noteId} className="body-small">
                    {note}
                  </span>
                )}
              </span>
              {control}
            </li>
          );
        })}
      </ul>
      {confirming && (
        <ConfirmDialog
          icon={BadgeCheck}
          tone="warning"
          {...autonomyConfirm(t, confirming, workKindName(t, confirming))}
          confirmLabel={t('autonomyConfirmAction')}
          onConfirm={() => void change(confirming, true)}
          onClose={() => setConfirming(undefined)}
        />
      )}
    </section>
  );
}
